import type { IVisitItem } from 'src/types/visit';

import useSWR from 'swr';
import { useMemo } from 'react';

import axios, { fetcher, endpoints } from 'src/utils/axios';

import { useAuthContext } from 'src/auth/hooks';
import { ActivityDTO } from 'src/types/dto';

const swrOptions = {
  revalidateIfStale: true,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
};

type VisitsData = {
  visits: IVisitItem[];
};

export function useGetVisits() {
  const url = endpoints.activities.list;

  const { data, isLoading, error, isValidating } = useSWR<VisitsData>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      visits: data?.visits || [],
      visitsLoading: isLoading,
      visitsError: error,
      visitsValidating: isValidating,
      visitsEmpty: !isLoading && !data?.visits,
    }),
    [data?.visits, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetVisitsForSpecificClient() {
  const { user } = useAuthContext();
  console.log('user', user);

  const isInspector = Array.isArray(user?.roles) && user?.roles.includes('inspector');

  const inspectorId = isInspector ? user?.inspector?.id : null;

  const url =
    isInspector && inspectorId
      ? `${endpoints.activities.root}?inspector-ids=${inspectorId}`
      : `${endpoints.activities.list}?clientId=${user?.client?.id}`;

  console.log('URL being used:', url);

  const { data, isLoading, error, isValidating, mutate } = useSWR<any>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(() => {
    const isUsingInspectorAPI = isInspector && inspectorId;
    let activities = isUsingInspectorAPI ? data?.data : data?.visits;

    console.log('Raw response data:', data);
    console.log('Extracted activities:', activities);

    if (activities) {
      activities = activities.map((activity: any) => ({
        ...activity,

        appointments: activity.appointments || [],
        activityInspectors: activity.activityInspectors || [],
        competencies: activity.competencies || [],
        allocatedInspectors: activity.allocatedInspectors || [],
      }));
    }

    return {
      visits: activities?.sort((a: any, b: any) => b.id - a.id) || [],
      visitsLoading: isLoading,
      visitsError: error,
      visitsValidating: isValidating,
      visitsEmpty: !isLoading && !activities,
      refetchVisits: mutate,

      pagination: isUsingInspectorAPI ? data?.meta : null,
    };
  }, [data, error, isLoading, isValidating, mutate, isInspector, inspectorId]);

  return memoizedValue;
}

type VisitData = {
  visit: IVisitItem;
};

export function useGetVisit(visitId: string, solutionId?: number | string) {
  const url = visitId 
    ? solutionId 
      ? `${endpoints.activities.details(visitId)}?solutionId=${solutionId}`
      : endpoints.activities.details(visitId)
    : '';

  const { data, isLoading, error, isValidating, mutate } = useSWR<ActivityDTO>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      visit: data,
      visitLoading: isLoading,
      visitError: error,
      visitValidating: isValidating,
      mutateVisit: mutate
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

type SearchResultsData = {
  results: IVisitItem[];
};

export function useSearchVisits(query: string) {
  const url = query ? [endpoints.activities.search, { params: { query } }] : '';

  const { data, isLoading, error, isValidating } = useSWR<SearchResultsData>(url, fetcher, {
    ...swrOptions,
    keepPreviousData: true,
  });

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.results || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
      searchEmpty: !isLoading && !data?.results.length,
    }),
    [data?.results, error, isLoading, isValidating]
  );

  return memoizedValue;
}

/**
 * Exports visits with the given filters and parameters.
 * @param params - Export parameters including filters and pagination
 * @returns Blob data for download
 */
export async function exportVisits(params: {
  page: number;
  take: number;
  search?: string;
  statuses?: string;
  'standard-ids'?: string;
  'competencies-ids'?: string;
  regions?: string;
  'target-months'?: string;
  'due-date'?: string;
  types?: string;
  'customer-ids'?: string;
  priorities?: string;
  'project-references'?: string;
  'is-allocated'?: string;
  'solution-ids'?: string;
  'inspector-ids'?: string;
}): Promise<Blob> {
  try {
    const timestamp = new Date().getTime();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    
    queryParams.append('_', String(timestamp));
    
    const response = await axios.get(`${endpoints.activities.root}?${queryParams.toString()}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv',
        'Cache-Control': 'no-cache, no-store',
        Pragma: 'no-cache',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error exporting visits:', error);
    throw error;
  }
}


export async function requestActivityExport(filters: Record<string, unknown>): Promise<{
  exportId: number;
  status: string;
}> {
  const response = await axios.post<{ exportId: number; status: string }>(
    endpoints.activities.export,
    filters ?? {},
  );
  return response.data;
}

export async function downloadActivityExport(
  exportId: number,
  fallbackFileName?: string,
): Promise<void> {
  const response = await axios.get(endpoints.activities.exportDownload(exportId), {
    responseType: 'blob',
  });

  const contentDisposition = response.headers['content-disposition'];
  let fileName = fallbackFileName || `activities_export_${exportId}.csv`;
  if (typeof contentDisposition === 'string') {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match?.[1]) {
      fileName = match[1];
    }
  }

  const blob = response.data as Blob;
  const objectUrl = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
}

export function useGetVisitsByProject(projectId?: string | number) {
  const hasValidId = projectId !== undefined && projectId !== null && projectId !== '';
  const url = hasValidId ? `${endpoints.activities.byProject(String(projectId))}` : null;

  const { data, isLoading, error, isValidating } = useSWR<any>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(() => {
    const activities = Array.isArray(data) ? data : data?.data || [];
    
    return {
      visits: activities,
      visitsLoading: isLoading,
      visitsError: error,
      visitsValidating: isValidating,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}